/**
 * Platform-wide money formatter (web).
 *
 * Emits money as "<ISO code> <amount>" — never a currency symbol — per the
 * 2026-08-15 decision. UK: "GBP 19.99" (not "£19.99"). Pakistan: "PKR 2500"
 * (not "₨2500" or "Rs. 2500"). UAE later: "AED 75.00".
 *
 * Why codes-not-symbols: '£' also = Egyptian pound; '₨' also = Nepalese /
 * Indian / Sri Lankan / Mauritian rupees; some Android/iOS fonts drop '₨'
 * to a fallback glyph. Matches how airlines / Stripe dashboards / global
 * invoices already price.
 *
 * Ship state (Phase 3): dark-deployed. Nothing calls this yet — call-site
 * swap is Phase 4, behind the `MultiRegionFlags.UseUnifiedMoneyFormat`
 * server flag. Existing hardcoded "£" output continues verbatim.
 *
 * Fallback: unknown / empty currencyCode → "GBP" (mid-rollout half-populated
 * data stays legible). Use `tryFormatMoney` if the caller wants to know
 * whether the fallback fired.
 */

const FALLBACK_CURRENCY = 'GBP';
const DEFAULT_LOCALE = 'en-GB';

const DECIMALS_BY_CURRENCY: Record<string, number> = {
  JPY: 0,
  KRW: 0,
  // everything else defaults to 2
};

function normaliseCode(currencyCode: string | null | undefined): string {
  if (!currencyCode || currencyCode.trim().length !== 3) return FALLBACK_CURRENCY;
  return currencyCode.trim().toUpperCase();
}

function decimalsFor(code: string): number {
  return DECIMALS_BY_CURRENCY[code] ?? 2;
}

function formatAmount(amount: number, locale: string, decimals: number): string {
  // Force currencyDisplay:'code' would put the ISO code in front (built-in),
  // but Intl.NumberFormat's currency style still uses currency-specific
  // decimal rules we don't want (e.g. it hides decimals for JPY). Simpler:
  // format the number as decimal + prepend the code ourselves.
  const nf = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return nf.format(amount);
}

/**
 * Format an amount as "<CODE> <amount>" (e.g. "GBP 19.99").
 * Silently falls back to GBP on null/empty currencyCode.
 */
export function formatMoney(
  amount: number,
  currencyCode: string | null | undefined,
  locale?: string,
): string {
  const code = normaliseCode(currencyCode);
  const decimals = decimalsFor(code);
  const number = formatAmount(amount, locale?.trim() || DEFAULT_LOCALE, decimals);
  return `${code} ${number}`;
}

export interface MoneyFormatResult {
  formatted: string;
  currencyCode: string;
  usedFallback: boolean;
}

/**
 * Same as `formatMoney` but returns a small result object so callers can
 * distinguish "used supplied currency" from "fell back to GBP because
 * input was missing". Useful for Sentry warnings during Phase 4 rollout
 * to catch call sites that forgot to thread currency through.
 */
export function tryFormatMoney(
  amount: number,
  currencyCode: string | null | undefined,
  locale?: string,
): MoneyFormatResult {
  const code = normaliseCode(currencyCode);
  const usedFallback = !(currencyCode && currencyCode.trim().length === 3);
  const decimals = decimalsFor(code);
  const number = formatAmount(amount, locale?.trim() || DEFAULT_LOCALE, decimals);
  return { formatted: `${code} ${number}`, currencyCode: code, usedFallback };
}
